import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { useKnownInstanceIds } from './portal/useKnownInstanceIds';

/**
 * The URL is where the instance scope lives, so the chat and the dashboard filter bar
 * share one storage instead of each keeping a copy. The helpers below are that single
 * definition of the param's name and format; this hook is the chat's view of it (values
 * validated against the user's instances), `useDashboardFilters` is the dashboard's.
 */
export const INSTANCE_IDS_PARAM = 'instance_ids';

export const readInstanceIdsParam = (searchParams: URLSearchParams): string[] =>
  decodeURIComponent(searchParams.get(INSTANCE_IDS_PARAM) ?? '')
    .split(',')
    .filter(Boolean);

/** Write (or clear, when empty — meaning "every accessible instance") the scope in place. */
export const writeInstanceIdsParam = (searchParams: URLSearchParams, instanceIds: string[]): void => {
  const value = instanceIds.join(',');
  if (value) {
    searchParams.set(INSTANCE_IDS_PARAM, value);
  } else {
    searchParams.delete(INSTANCE_IDS_PARAM);
  }
};

export const useChatUrlInstanceIds = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Candidates are validated against what the user can actually reach, so a stale or
  // hand-edited id in the URL never becomes a selection.
  const candidateIds = useMemo(() => readInstanceIdsParam(searchParams), [searchParams]);

  const { isKnown } = useKnownInstanceIds(candidateIds);
  const selectedInstanceIds = candidateIds.filter(isKnown);

  /** Set the chat's instance scope; an empty list drops the param (= every instance). */
  const setSelectedInstanceIds = useCallback(
    (instanceIds: string[]) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        writeInstanceIdsParam(next, instanceIds);
        return next;
      });
    },
    [setSearchParams],
  );

  return { selectedInstanceIds, setSelectedInstanceIds };
};
